import axios from 'axios'
import { ai_api } from './variables'

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 30000

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

// The suggestion endpoints compute results in the background for some apps and
// respond with status: "processing" ("call this SAME endpoint again in a few
// seconds to check progress") - poll the same URL until it's done or we hit
// the timeout. On timeout the last ("processing") response is returned so
// callers can tell "still generating" apart from "no results".
const pollSuggestionEndpoint = async (url, { intervalMs = POLL_INTERVAL_MS, timeoutMs = POLL_TIMEOUT_MS } = {}) => {
    const authHeaders = {
        headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
    }

    const startedAt = Date.now()
    let data = null

    while (Date.now() - startedAt < timeoutMs) {
        const response = await axios.get(url, authHeaders)
        data = response.data

        if (data?.status !== "processing") {
            return data
        }

        await sleep(intervalMs)
    }

    return data
}

// Fetches keyword suggestions for an app. Pass { more: true } to fetch
// additional keywords beyond the initial set (?more=true).
export const fetchKeywordSuggestions = (itemId, { more = false } = {}) =>
    pollSuggestionEndpoint(`${ai_api}/keyword-suggestion/suggest/${itemId}/${more ? '?more=true' : ''}`)

// Fetches AI genre suggestions for an app ({ suggestions: [{ genre }] }).
// Polled every 5s for up to 60s.
export const fetchGenreSuggestions = (itemId) =>
    pollSuggestionEndpoint(`${ai_api}/keyword-suggestion/genres/${itemId}`, { intervalMs: 5000, timeoutMs: 60000 })

// The API has returned `keywords` as either a flat array of strings or an
// array of { keyword, rank, out_of, competitors } objects - normalize both
// into the richer shape so consumers don't need to care which one came back.
export const normalizeKeywordItems = (rawKeywords) => {
    return (rawKeywords || [])
        .map(item => {
            if (typeof item === 'string') {
                return item ? { keyword: item, rank: null, out_of: null, competitors: [] } : null
            }
            if (!item?.keyword) return null
            return {
                keyword: item.keyword,
                rank: item.rank ?? null,
                out_of: item.out_of ?? null,
                competitors: item.competitors || [],
            }
        })
        .filter(Boolean)
}
